/**
 * "Criar outra oportunidade?": a pergunta do botão Criar oportunidade quando o
 * cliente já tem alguma (21/10/2026).
 *
 * Criar pela conversa era um clique, e a segunda oportunidade do mesmo cliente
 * nascia sem ninguém ver a primeira. Agora a tela diz onde a que existe está,
 * no funil e na etapa, e pergunta. Contam todas, em andamento, ganhas ou
 * perdidas (decisão de 21/10/2026): quem decide se a nova faz sentido é quem
 * atende, vendo a etapa da que já existe.
 */

import { useId, type ReactNode } from 'react';
import { Botao, Dialogo } from '@/shared/ui';
import { fraseDoLugar, type LugarDaOportunidade } from '../lib/lugar-da-oportunidade';

export interface OportunidadeExistente {
  readonly id: string;
  readonly titulo: string | null;
  readonly lugar: LugarDaOportunidade;
}

export function AvisoDeOportunidadeExistente({
  oportunidades,
  aoCriarOutra,
  aoFechar,
}: {
  oportunidades: readonly OportunidadeExistente[];
  aoCriarOutra: () => void;
  aoFechar: () => void;
}): ReactNode {
  const descricao = useId();
  const [unica] = oportunidades;

  return (
    <Dialogo
      titulo="Criar outra oportunidade?"
      papel="alertdialog"
      descricaoId={descricao}
      aoFechar={aoFechar}
      botoes={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            Não
          </Botao>
          <Botao onClick={aoCriarOutra}>Sim, criar outra</Botao>
        </>
      }
    >
      {oportunidades.length === 1 && unica ? (
        <p id={descricao}>
          {comLugar(
            'Você já tem uma oportunidade associada a este cliente',
            fraseDoLugar(unica.lugar),
            ' ',
          )}{' '}
          Deseja criar outra?
        </p>
      ) : (
        <>
          <p id={descricao}>
            Você já tem {oportunidades.length} oportunidades associadas a este cliente. Deseja criar
            outra?
          </p>
          <ul>
            {oportunidades.map((oportunidade) => (
              <li key={oportunidade.id}>
                {comLugar(
                  `"${oportunidade.titulo ?? 'Sem título'}"`,
                  fraseDoLugar(oportunidade.lugar),
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </Dialogo>
  );
}

/** A frase, com o lugar quando se sabe, e o ponto final. */
function comLugar(inicio: string, lugar: string, separador = ', '): string {
  return lugar ? `${inicio}${separador}${lugar}.` : `${inicio}.`;
}
